import { t } from '@lingui/core/macro';

/**
 * Option lists for the researcher account settings forms.
 *
 * Industries and company sizes are copied from the legacy researcher app
 * (`website/apps/shared/constants/employment.js`) because the stored values
 * must keep matching what legacy wrote and what the legacy project form
 * filters on. The industry list omits legacy's "All Industries" entry, which
 * is a project-targeting option rather than something a person works in.
 */

export const INDUSTRY_NAMES = [
  'Airlines/Aviation',
  'Alternative Dispute Resolution',
  'Alternative Medicine',
  'Animation',
  'Apparel & Fashion',
  'Architecture & Planning',
  'Arts and Crafts',
  'Automotive',
  'Aviation & Aerospace',
  'Banking',
  'Biotechnology',
  'Broadcast Media',
  'Building Materials',
  'Business Supplies and Equipment',
  'Capital Markets',
  'Chemicals',
  'Civic & Social Organization',
  'Civil Engineering',
  'Commercial Real Estate',
  'Computer & Network Security',
  'Computer Games',
  'Computer Hardware',
  'Computer Networking',
  'Computer Software',
  'Construction',
  'Consumer Electronics',
  'Consumer Goods',
  'Consumer Services',
  'Cosmetics',
  'Dairy',
  'Defense & Space',
  'Design',
  'Education Management',
  'E-Learning',
  'Electrical/Electronic Manufacturing',
  'Entertainment',
  'Environmental Services',
  'Events Services',
  'Executive Office',
  'Facilities Services',
  'Farming',
  'Financial Services',
  'Fine Art',
  'Fishery',
  'Food & Beverages',
  'Food Production',
  'Fund-Raising',
  'Furniture',
  'Gambling & Casinos',
  'Glass, Ceramics & Concrete',
  'Government Administration',
  'Government Relations',
  'Graphic Design',
  'Health, Wellness and Fitness',
  'Higher Education',
  'Hospital & Health Care',
  'Hospitality',
  'Human Resources',
  'Import and Export',
  'Individual & Family Services',
  'Industrial Automation',
  'Information Services',
  'Information Technology and Services',
  'Insurance',
  'International Affairs',
  'International Trade and Development',
  'Internet',
  'Investment Banking',
  'Investment Management',
  'Judiciary',
  'Law Enforcement',
  'Law Practice',
  'Legal Services',
  'Legislative Office',
  'Leisure, Travel & Tourism',
  'Libraries',
  'Logistics and Supply Chain',
  'Luxury Goods & Jewelry',
  'Machinery',
  'Management Consulting',
  'Maritime',
  'Market Research',
  'Marketing and Advertising',
  'Mechanical or Industrial Engineering',
  'Media Production',
  'Medical Devices',
  'Medical Practice',
  'Mental Health Care',
  'Military',
  'Mining & Metals',
  'Motion Pictures and Film',
  'Museums and Institutions',
  'Music',
  'Nanotechnology',
  'Newspapers',
  'Non-Profit Organization Management',
  'Oil & Energy',
  'Online Media',
  'Outsourcing/Offshoring',
  'Package/Freight Delivery',
  'Packaging and Containers',
  'Paper & Forest Products',
  'Performing Arts',
  'Pharmaceuticals',
  'Philanthropy',
  'Photography',
  'Plastics',
  'Political Organization',
  'Primary/Secondary Education',
  'Printing',
  'Professional Training & Coaching',
  'Program Development',
  'Public Policy',
  'Public Relations and Communications',
  'Public Safety',
  'Publishing',
  'Railroad Manufacture',
  'Ranching',
  'Real Estate',
  'Recreational Facilities and Services',
  'Religious Institutions',
  'Renewables & Environment',
  'Research',
  'Restaurants',
  'Retail',
  'Security and Investigations',
  'Semiconductors',
  'Shipbuilding',
  'Sporting Goods',
  'Sports',
  'Staffing and Recruiting',
  'Supermarkets',
  'Telecommunications',
  'Textiles',
  'Think Tanks',
  'Tobacco',
  'Translation and Localization',
  'Transportation/Trucking/Railroad',
  'Utilities',
  'Venture Capital & Private Equity',
  'Veterinary',
  'Warehousing',
  'Wholesale',
  'Wine and Spirits',
  'Wireless',
  'Writing and Editing',
] as const;

/** Codes accepted by core-api's `validateCompanySize` (minus the targeting-only `all`). */
export const COMPANY_SIZE_CODES = [
  'selfemployed',
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  '10000+',
] as const;

export type CompanySizeCode = (typeof COMPANY_SIZE_CODES)[number];

export function companySizeLabel(code: CompanySizeCode): string {
  switch (code) {
    case 'selfemployed':
      return t`Self employed`;
    case '1-10':
      return '1 - 10';
    case '11-50':
      return '11 - 50';
    case '51-200':
      return '51 - 200';
    case '201-500':
      return '201 - 500';
    case '501-1000':
      return '501 - 1,000';
    case '1001-5000':
      return '1,001 - 5,000';
    case '5001-10000':
      return '5,001 - 10,000';
    case '10000+':
      return '10,000+';
  }
}

export function isCompanySizeCode(value: string): value is CompanySizeCode {
  return COMPANY_SIZE_CODES.some((code) => code === value);
}

/** The email notifications a researcher can switch off, in display order. */
export const RESEARCHER_NOTIFICATION_KEYS = [
  'newMessage',
  'booking',
  'cancellation',
  'firstQualified',
  'autocomplete',
] as const;

export type ResearcherNotificationKey =
  (typeof RESEARCHER_NOTIFICATION_KEYS)[number];

export type ResearcherNotificationSettings = Record<
  ResearcherNotificationKey,
  boolean
>;
